import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AuthLayout } from '@/components/AuthLayout'
import { supabase } from '@/lib/supabaseClient'
import { Telemetry } from '@/lib/telemetry'
import { COMMONS_AUTH_ERROR_KEY } from '@/lib/commonsAccess'

export function SigninPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // A2F-587: read + clear the flash set by AuthContext when commons access
  // was denied or when a signed-in user was ejected for lacking membership.
  useEffect(() => {
    const flash = sessionStorage.getItem(COMMONS_AUTH_ERROR_KEY)
    if (flash) {
      setError(flash)
      sessionStorage.removeItem(COMMONS_AUTH_ERROR_KEY)
    }
  }, [])

  const from = (location.state as { from?: string } | null)?.from ?? '/'

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error: err } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })
    setLoading(false)

    if (err) {
      setError('Invalid email or password.')
      const reason =
        err.message?.toLowerCase().includes('network') ||
        err.message?.toLowerCase().includes('fetch')
          ? 'network'
          : err.message?.toLowerCase().includes('invalid')
            ? 'invalid_credentials'
            : 'other'
      Telemetry.signInFailed({ reason })
      return
    }
    Telemetry.userSignedIn({ auth_method: 'email' })
    navigate(from, { replace: true })
  }

  return (
    <AuthLayout title="Sign in" subtitle="Access your CTDCommons account.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-xs font-medium text-slate-600 mb-1"
          >
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>

        {error && (
          <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2 bg-brand-orange text-white text-sm font-medium rounded-md hover:bg-brand-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </form>

      {/*
        A2F-626: static hint for enterprise SSO users who'd otherwise hit
        "Invalid email or password" because their main-app Supabase account
        was provisioned via Entra ID and has no password.

        MUST be shown unconditionally — do not branch on the typed email or
        response. Branching leaks email-registration state, breaking the
        enumeration resistance the auth flow is hardened against.
      */}
      <div className="mt-4 p-3 rounded-md bg-slate-50 border border-slate-200 text-xs text-slate-600 leading-relaxed">
        <span className="font-medium text-slate-700">Enterprise Chrona user?</span>{' '}
        Commons uses email + password (separate from your work SSO).{' '}
        <Link
          to="/auth/signup"
          onClick={() => Telemetry.loginHintClicked()}
          className="text-brand-orange hover:underline whitespace-nowrap"
        >
          Create a commons password →
        </Link>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <Link to="/auth/reset-password" className="text-brand-orange hover:underline">
          Forgot password?
        </Link>
        <Link
          to="/auth/signup"
          onClick={() => Telemetry.createAccountCtaClicked({ placement: 'login_card' })}
          className="text-brand-orange hover:underline"
        >
          Create free account →
        </Link>
      </div>
    </AuthLayout>
  )
}
